// components/common/MaintenanceModal/MaintenanceModal.tsx
import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Button,
} from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { DarkModeContext } from "../../../context/darkModeContext";
interface StyledButtonProps {
  buttonType?: "primary" | "secondary";
}
const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "buttonType",
})<StyledButtonProps>(({ theme, buttonType }) => ({
  minWidth: "120px",
  padding: "8px 24px",
  fontWeight: 600,
  ...(buttonType === "primary" && {
    backgroundColor: blue[600],
    color: "white",
    "&:hover": {
      backgroundColor: blue[700],
    },
    "&:disabled": {
      backgroundColor: blue[300],
      color: "white",
    },
  }),
  ...(buttonType === "secondary" && {
    backgroundColor: grey[100],
    color: grey[800],
    "&:hover": {
      backgroundColor: grey[200],
    },
    border: `1px solid ${grey[300]}`,
  }),
}));
interface MaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { Nombre: string; Activo: boolean }) => Promise<void>;
  title: string;
  initialData?: {
    Nombre: string;
    Activo: boolean;
  };
  isLoading: boolean;
  isEdit?: boolean;
}
const MaintenanceModal = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialData,
  isLoading,
  isEdit = false,
}: MaintenanceModalProps) => {
  const [formData, setFormData] = useState({
    Nombre: "",
    Activo: true,
  });
  const { darkMode } = useContext(DarkModeContext);
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({ Nombre: "", Activo: true });
    }
  }, [initialData, isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      Nombre: formData.Nombre,
      Activo: isEdit ? initialData?.Activo || true : formData.Activo,
    });
  };
  const handleClose = () => {
    // Asegurarse que no haya modales de SweetAlert abiertos
    if (!document.querySelector(".swal2-shown")) {
      onClose();
    }
  };
  return (
    <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            position: "relative",
            zIndex: 1200,
          },
          "& .MuiBackdrop-root": {
            zIndex: 1100,
          },
        }}
        PaperProps={{
          elevation: 24,
          sx: {
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
            fontSize: "1.25rem",
            fontWeight: 500,
          }}
        >
          {title}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Nombre"
              type="text"
              fullWidth
              value={formData.Nombre}
              onChange={(e) =>
                setFormData({ ...formData, Nombre: e.target.value })
              }
              required
              variant="outlined"
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                },
              }}
            />
            {!isEdit && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.Activo}
                    onChange={(e) =>
                      setFormData({ ...formData, Activo: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Activo"
                sx={{
                  mt: 1,
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: blue[600],
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: blue[600],
                  },
                }}
              />
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: "divider",
              gap: 1,
            }}
          >
            <ActionButton
              onClick={onClose}
              disabled={isLoading}
              buttonType="secondary"
              sx={{
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              type="submit"
              buttonType="primary"
              disabled={isLoading}
              sx={{
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {isLoading ? "Guardando..." : isEdit ? "Modificar" : "Guardar"}
            </ActionButton>
          </DialogActions>
        </form>
      </Dialog>
  );
};
export default MaintenanceModal;
